import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import relativeTime from "dayjs/plugin/relativeTime";
import isoWeek from "dayjs/plugin/isoWeek";
import { isNaN } from "lodash";
import { isAllLocationSelected } from "../library/helpers";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
// ISO week → weeks always start on Monday (Mon–Sun), regardless of locale.
dayjs.extend(isoWeek);

export const getCurrentDateTimeUTC = () => {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, "0");
  const day = String(now.getUTCDate()).padStart(2, "0");
  const hours = String(now.getUTCHours()).padStart(2, "0");
  const minutes = String(now.getUTCMinutes()).padStart(2, "0");
  const seconds = String(now.getUTCSeconds()).padStart(2, "0");
  const formattedDateTime = `${year}${month}${day}${hours}${minutes}${seconds}`;
  return formattedDateTime;
};

export const DATE_TIME_FORMATS = {
  MMM_DD_YYYY_hh_mm_A: "MMM DD, YYYY hh:mm A", // Example: "Jul 28, 2023 11:01 PM"
  DD_MMM_YYYY_hh_mm_A: "DD MMM YYYY, hh:mm A", // Example: "28 Jul 2023, 11:01 PM"
  DD_MMM_YYYY: "DD MMM YYYY", // Example: "28 Jul 2023"
  MMMM_D_YYYY: "MMMM D, YYYY", // Example: "July 28, 2023"
  YYYY_MM_DD: "YYYY-MM-DD", // Example: "2023-07-28"
  DDDD: "dddd", // Example: "Monday"
  HH_MM_A: "hh:mm A", // Example: "11:01 PM"
  HH_MM_SS: "HH:mm:ss", // Example: "23:01:45"
  ddd_MMM_Do_YYYY_hh_mm_A: "ddd, MMM Do YYYY, h:mm A", // Example: "Mon, Jul 28th 2023, 11:01 PM"
  h_mm_A: "h:mm A", // Example: "11:01 PM"
  YYYY_MM: "YYYY-MM", // Example: "2023-07"
  MMM_DD_YYYY: "MMM D, YYYY", // Example: "Jul 28, 2023"
  MMM_DD_hh_mm_A: "MMM DD, hh:mm A", // Example: "Jul 28, 11:01 PM"
  MMM_DD_YYYY_COMMA_hh_mm_A: "MMM DD, YYYY, hh:mm A",
  MMM_DD_h_mm_A: "MMM D, h:mm A", // Example: "Jul 28, 11:01 PM"
  MMM_DD_YYYY_AT_hh_mm_A: "MMM DD, YYYY, [at] hh:mm A", // Example: "Jul 28, 2023, at 11:01 PM"
  MMMM_D_h_mm_A: "MMMM D, h:mm A", // December 1, 9:30 AM
  MM_DD_YYYY_hh_mm_A: "MM/DD/YYYY hh:mm A", // Example: "11/25/2025 08:35 AM"
  MMM_DD_YYYY_PIPE_h_mm_A: "MMM DD, YYYY | h:mm A", // Example: "Jun 14, 2024 | 10:53 AM"
  MMM_D_YYYY_hh_mm_A: "MMM D, YYYY, h:mm A", // Example: "Jul 5, 2023, 9:05 AM"
  MMM_D_YYYY_DOT_h_mm_A: "MMM D, YYYY · h:mm A", // Example: "Aug 5, 2026 · 3:45 PM"
  YYYY_MM_DD_T_HH_MM_SS: "YYYY-MM-DDTHH:mm:ss", // Example: "2023-07-28T23:01:45"
  YYYY_MM_DD_h_mm_A: "YYYY-MM-DD h:mm A", // Example: "2023-07-28 11:01 PM"
  MM_DD_YYYY: "MM/DD/YYYY", // Example: "07/28/2023"
};

export const formatDateTime = (date, format = DATE_TIME_FORMATS.MMM_DD_YYYY_hh_mm_A) => {
  if (!date) return "";
  return dayjs(date).format(format);
};

/**
 * Formats a date timestamp.
 * @param {string|Date} date - The date to format.
 * @param {string} format - The format string (default: "DD MMM, YYYY | hh:mm a").
 * @param {string} timezone - Optional timezone to convert to (e.g., "America/New_York").
 * @returns {string} - The formatted date string.
 */
// eslint-disable-next-line default-param-last
export const formatDate = (date, format = "DD MMM, YYYY | hh:mm a", timezone) => {
  if (!date) return "--";
  if (timezone) {
    return dayjs.utc(date).tz(timezone).format(format);
  }
  return dayjs(date).format(format);
};

/**
 * Formats a server timestamp in the viewer's local timezone.
 *
 * The API returns naive UTC strings ("2026-08-21T10:30:00"), which dayjs would
 * otherwise read as local time and render off by the viewer's offset — so the
 * value is parsed as UTC first. Same idiom as the lead list and email list.
 *
 * @param {string|Date} date - UTC timestamp from the API.
 * @param {string} format - one of DATE_TIME_FORMATS.
 * @returns {string} formatted local time, or "--" when there is nothing to show.
 */
export const formatUtcToLocal = (date, format = DATE_TIME_FORMATS.MMM_DD_YYYY_COMMA_hh_mm_A) => {
  if (!date) return "--";
  const parsed = dayjs.utc(date);
  return parsed.isValid() ? parsed.local().format(format) : "--";
};

/**
 * Resolves the timezone of the user's currently-selected location.
 *
 * Reads the active location id from sessionStorage ("currentLocationId") and
 * matches it against the user's `locations`, returning that location's
 * timezone (e.g. "America/Chicago"). Returns undefined when there is no
 * matching location so callers can fall back to the viewer's local zone.
 *
 * @param {Object} user - the current user (expects `user.locations[].location`).
 * @returns {string|undefined} IANA timezone string, or undefined.
 */
export const getCurrentLocationTimezone = user => {
  const currentLocationId = sessionStorage.getItem("currentLocationId");
  return user?.locations?.find(loc => +loc?.location?.id === +currentLocationId)?.location
    ?.timezone;
};

/**
 * Formats a server UTC timestamp in the selected location's timezone.
 *
 * The pairing most screens want: resolve the active location's zone, and fall
 * back to the viewer's own when there is no single location selected. Worth a
 * helper because the naive fallback is wrong — `formatDate` without a timezone
 * hands the string to `dayjs()`, which reads the API's naive UTC
 * ("2026-08-21T10:30:00") as local time and renders it off by the viewer's
 * offset. `formatUtcToLocal` parses as UTC first, so it is the correct default.
 *
 * @param {string|Date} date - UTC timestamp from the API.
 * @param {Object} user - current user, for resolving the active location.
 * @param {string} format - one of DATE_TIME_FORMATS.
 * @returns {string} the formatted time, or "--" when there is nothing to show.
 */
export const formatUtcToLocationTime = (
  date,
  user,
  format = DATE_TIME_FORMATS.MMM_DD_YYYY_COMMA_hh_mm_A
) => {
  if (!date) return "--";
  const timezone = getCurrentLocationTimezone(user);
  return timezone ? formatDate(date, format, timezone) : formatUtcToLocal(date, format);
};

/**
 * A stored UTC timestamp as the calendar date a *picker* should show, read in
 * the given timezone.
 *
 * Pickers need a dayjs value, not a string, and the date has to be resolved in
 * the location's zone before it is reduced to a day: an instant near midnight
 * UTC is a different calendar date at a US dealership, so taking the date first
 * lands a day out.
 *
 * @param {string|Date} value - UTC timestamp from the API.
 * @param {string} [timezone] - IANA zone; the viewer's own when omitted.
 * @returns {dayjs.Dayjs|null} the date, or null when there is nothing to show.
 */
export const utcToZonedDate = (value, timezone) => {
  if (!value) return null;
  const formatted = formatDate(value, DATE_TIME_FORMATS.YYYY_MM_DD, timezone);
  return formatted && formatted !== "--" ? dayjs(formatted, DATE_TIME_FORMATS.YYYY_MM_DD) : null;
};

/**
 * Returns ISO8601 string (e.g. "2024-06-10T00:00:00Z") for a given date.
 * @param {string|Date|dayjs.Dayjs} date
 * @returns {string}
 */
export const toISO8601 = date => {
  if (!date) return "";
  return dayjs(date).toISOString();
};

/**
 * Formats a time string (e.g. "09:00:00") to 12-hour format (e.g. "09:00 AM").
 * @param {string} timeStr
 * @param {string} format (optional) - defaults to DATE_TIME_FORMATS.HH_MM_A
 * @returns {string}
 */
export const formatTimeTo12Hour = (timeStr, format = DATE_TIME_FORMATS.HH_MM_A) => {
  if (!timeStr) return "";
  return dayjs(timeStr, "HH:mm:ss").format(format);
};

// Format seconds to a readable duration broken into years, months, days,
// hours, minutes, and seconds. Every non-zero unit is shown — small
// remainders are preserved so totals add up (e.g. a 14-day, 20-minute,
// 45.5-second duration renders as "14d 20m 45s", not "14d 20m").
//
// Two modes:
//   - default (rough): seconds floored, no fractional part.
//       14d 20m 45.5s   → "14d 20m 45s"
//       523211.4s       → "6d 1h 20m 11s"
//       65.4s           → "1m 5s"
//   - exactTime=true: fractional seconds preserved (rounded to 2 decimals).
//       14d 20m 45.5s   → "14d 20m 45.5s"
//       523211.4s       → "6d 1h 20m 11.4s"
//
// A "year" is 365 days and a "month" is 30 days — the calendar varies but
// the dashboards just need a coarse roll-up so multi-year/multi-month
// durations don't read as a giant day count. `mo` is used for months to
// avoid colliding with `m` (minutes).
//
// Used by both the Media Detail Report (`formatTimeInSeconds`) and Response
// Time metrics (`formatResponseTime`).
export const formatDurationToReadable = (seconds, exactTime = false) => {
  if (!seconds) return "0s";

  const totalSeconds = parseFloat(seconds);
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return "0s";

  const SECONDS_PER_MINUTE = 60;
  const SECONDS_PER_HOUR = 3600;
  const SECONDS_PER_DAY = 86400;
  const SECONDS_PER_MONTH = 30 * SECONDS_PER_DAY;
  const SECONDS_PER_YEAR = 365 * SECONDS_PER_DAY;

  const years = Math.floor(totalSeconds / SECONDS_PER_YEAR);
  const months = Math.floor((totalSeconds % SECONDS_PER_YEAR) / SECONDS_PER_MONTH);
  const days = Math.floor((totalSeconds % SECONDS_PER_MONTH) / SECONDS_PER_DAY);
  const hours = Math.floor((totalSeconds % SECONDS_PER_DAY) / SECONDS_PER_HOUR);
  const minutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const remainderSeconds = totalSeconds % SECONDS_PER_MINUTE;
  const secondsValue = exactTime
    ? Number(remainderSeconds.toFixed(2))
    : Math.floor(remainderSeconds);

  const parts = [];
  if (years > 0) parts.push(`${years}y`);
  if (months > 0) parts.push(`${months}mo`);
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secondsValue > 0) parts.push(`${secondsValue}s`);

  return parts.length ? parts.join(" ") : "0s";
};

/**
 * Formats response time from minutes to readable duration.
 * Use this when API returns time in minutes (common for response time metrics).
 * @param {number|string} minutes - Time value in minutes.
 * @param {boolean} [exactTime=false] - When true, render every non-zero unit
 *   (e.g. "6d 1h 20m 11s"). Otherwise rough mode keeps the top 2 units only.
 * @returns {string} Formatted duration.
 */
export const formatResponseTime = (minutes, exactTime = false) => {
  if (!minutes || minutes === 0) return "0s";
  const seconds = parseFloat(minutes) * 60;
  return formatDurationToReadable(seconds, exactTime);
};

/**
 * Formats time in minutes to readable duration format.
 * Generic utility for converting minutes to human-readable format.
 * @param {number|string} minutes - Time value in minutes (can be decimal like 1640.96)
 * @returns {string} Formatted duration (e.g., "27h 20m 57s", "1h 30m")
 */
export const formatTimeInMinutes = minutes => {
  if (!minutes || minutes === 0) return "0m";

  // Convert minutes to seconds
  const totalSeconds = parseFloat(minutes) * 60;

  const hours = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = Math.floor(totalSeconds % 60);

  let result = "";

  if (hours > 0) {
    result += `${hours}h `;
  }

  if (mins > 0 || hours > 0) {
    result += `${mins}m`;
  }

  if (secs > 0 && hours === 0) {
    // Only show seconds if less than 1 hour
    result += ` ${secs}s`;
  }

  return result.trim();
};

/**
 * Formats time in seconds to readable duration format.
 * Delegates to `formatDurationToReadable` so Media Detail Report durations
 * and Response Time metrics share the same days/hours/minutes/seconds
 * breakdown and decimal-second handling.
 * @param {number|string} seconds - Time value in seconds.
 * @param {boolean} [exactTime=false] - When true, render every non-zero unit
 *   (e.g. "6d 1h 20m 11s"). Otherwise rough mode keeps the top 2 units only.
 * @returns {string} Formatted duration.
 */
export const formatTimeInSeconds = (seconds, exactTime = false) =>
  formatDurationToReadable(seconds, exactTime);

/**
 * Convert a timestamp (ISO string, Date, or Unix) into a relative time string.
 *
 * @param {string|number|Date} input - Timestamp (ISO string, JS Date, or Unix in ms).
 * @returns {string} Relative time (e.g., "2 days ago", "in 3 months").
 */
export const timeAgo = input => {
  if (!input) return "";

  const date = dayjs(input);

  if (!date.isValid()) {
    return "Invalid date";
  }

  return date.fromNow(); // e.g., "2 days ago"
};

/**
 * A timestamp as people scan an activity feed: relative while recent
 * ("3 hours ago", "a day ago", "2 days ago"), the full date + time once older.
 *
 * @param {string|Date} date - Timestamp (ISO UTC from the API).
 * @param {Object} [options]
 * @param {number} [options.withinHours=48] - Below this age, render relative.
 * @param {string} [options.format] - One of DATE_TIME_FORMATS for older dates.
 * @param {string} [options.timezone] - IANA zone for the full date (viewer's own when omitted).
 * @returns {string} the label, or "--" when there is nothing to show.
 */
export const formatRecentOrDateTime = (
  date,
  { withinHours = 48, format = DATE_TIME_FORMATS.MMM_D_YYYY_hh_mm_A, timezone } = {}
) => {
  if (!date) return "--";
  const parsed = dayjs(date);
  if (!parsed.isValid()) return "--";
  if (dayjs().diff(parsed, "hour", true) < withinHours) return parsed.fromNow();
  return timezone ? formatDate(date, format, timezone) : formatUtcToLocal(date, format);
};

/**
 * Convert server timestamp (UTC, without offset) into local ISO string.
 *
 * @param timestamp - e.g. "2025-09-08T05:51:01.694623"
 * @returns Local ISO timestamp string
 */
export function normalizeServerTimestamp(timestamp) {
  if (!timestamp) return "";

  // Parse as UTC → convert to local → return ISO string
  return dayjs.utc(timestamp).local().toISOString();
}
export const isValidYear = year => {
  if (!year || year === 0 || year === "0") return false;

  const parsedYear = parseInt(year, 10);

  // Check if year is a valid number using dayjs
  const yearDate = dayjs(`${parsedYear}-01-01`);

  // Just validate if it's a valid year
  return yearDate.isValid() && !isNaN(parsedYear);
};

// ========== APPOINTMENT DATE UTILITIES ==========

/**
 * Convert a local date-time string to a UTC-formatted string.
 * @param {string} localDateStr - Local date-time string (e.g. "2026-04-14 10:30:00").
 * @param {string} tz - IANA timezone (e.g. "America/Los_Angeles").
 * @param {string} format - Output format (default: "YYYY-MM-DDTHH:mm:ss").
 * @returns {string} UTC string e.g. "2026-04-14T17:30:00"
 */
export const toUTCDateTime = (
  localDateStr,
  tz,
  format = DATE_TIME_FORMATS.YYYY_MM_DD_T_HH_MM_SS
) => {
  return dayjs.tz(localDateStr, tz).utc().format(format);
};

// ========== TIME PICKER UTILITIES ==========

/**
 * Get the next hour from current time, rounded to the start of the hour
 * @returns {dayjs.Dayjs} Next hour timestamp
 */
export const getNextHour = () => {
  return dayjs().add(1, "hour").startOf("hour");
};

/**
 * Check if a date is in the past (before today)
 * @param {dayjs.Dayjs|string|Date} date - Date to check
 * @returns {boolean} True if date is before today
 */
export const isPastDate = date => {
  if (!date) return false;
  return dayjs(date).isBefore(dayjs().startOf("day"));
};

/**
 * Check if a datetime is in the past
 * @param {dayjs.Dayjs|string|Date} date - Date to check
 * @param {dayjs.Dayjs|string|Date} time - Time to check (optional, uses date if not provided)
 * @returns {boolean} True if datetime is before current time
 */
export const isPastDateTime = (date, time) => {
  if (!date) return false;

  let dateTime;
  if (time && typeof time === "object" && time.hour) {
    // If time is a dayjs object with hour/minute methods
    dateTime = dayjs(date).hour(time.hour()).minute(time.minute());
  } else if (time) {
    // If time is provided separately
    dateTime = dayjs(`${dayjs(date).format("YYYY-MM-DD")} ${dayjs(time).format("HH:mm:ss")}`);
  } else {
    // Use date as complete datetime
    dateTime = dayjs(date);
  }

  return dateTime.isBefore(dayjs());
};

/**
 * Get disabled dates for date picker (all past dates)
 * @param {dayjs.Dayjs} current - Current date being evaluated
 * @returns {boolean} True if date should be disabled
 */
export const getDisabledDates = current => {
  return current && current < dayjs().startOf("day");
};

/**
 * Get disabled time configuration for time picker
 * @param {dayjs.Dayjs} selectedDate - Currently selected date
 * @returns {object} Configuration object for disabled hours/minutes
 */
export const getDisabledTime = selectedDate => {
  // If selected date is today, disable past hours
  if (selectedDate && selectedDate.isSame(dayjs(), "day")) {
    const currentHour = dayjs().hour();
    const currentMinute = dayjs().minute();

    return {
      disabledHours: () => {
        const hours = [];
        for (let i = 0; i < currentHour; i++) {
          hours.push(i);
        }
        return hours;
      },
      disabledMinutes: selectedHour => {
        if (selectedHour === currentHour) {
          const minutes = [];
          for (let i = 0; i <= currentMinute; i++) {
            minutes.push(i);
          }
          return minutes;
        }
        return [];
      },
    };
  }
  return {};
};

// ========== WORKING-HOURS SCHEDULING ==========
//
// Shared by the message and email schedulers so both offer the same slots:
// only working days, only future days, and only hours the location is open.

// "09:30:00" → { hour: 9, minute: 30 }. Parsed by hand on purpose — dayjs needs
// the customParseFormat plugin to read that string, and without it
// `dayjs("09:00:00", "HH:mm:ss")` is Invalid Date, which silently disables
// nothing at all.
const parseWorkingTime = value => {
  const [hour, minute] = String(value || "")
    .split(":")
    .map(Number);
  return {
    hour: Number.isFinite(hour) ? hour : null,
    minute: Number.isFinite(minute) ? minute : 0,
  };
};

/** The working-hours row for a given date, or undefined. */
export const getWorkingDayFor = (workingHours, date) => {
  if (!date || !Array.isArray(workingHours)) return undefined;
  const weekDay = dayjs(date).format("dddd").toUpperCase();
  return workingHours.find(entry => entry?.weekDay === weekDay);
};

/**
 * "Now" as the SCHEDULE's timezone sees it — the location's clock, not the
 * viewer's. A schedule is picked in the location's zone, so what counts as
 * today, and as already past, has to be judged there too: an IST user looking
 * at a Denver rooftop is ~11 hours ahead, which otherwise greys out that
 * rooftop's whole working day.
 */
const nowInZone = zone => {
  if (!zone) return dayjs();
  try {
    return dayjs().tz(zone);
  } catch (e) {
    // Unknown zone string — fall back rather than blank the picker.
    return dayjs();
  }
};

/** Calendar day of a picked value, as plain digits — no zone conversion. */
const dayKey = value => dayjs(value).format(DATE_TIME_FORMATS.YYYY_MM_DD);

/**
 * `disabledDate` for a picker: blocks past days and non-working days.
 * While `workingHours` is still loading every date is disabled, so a user can't
 * pick a slot we haven't validated yet.
 *
 * @param {Array}  workingHours rows of { weekDay, isWorkingDay, startTime, endTime }
 * @param {string} zone         the schedule's timezone, so "past" is judged there
 */
export const getWorkingHoursDisabledDate = (workingHours, zone) => current => {
  if (!current) return false;
  // Compared as calendar digits: the picked value carries the viewer's
  // offset, so `isBefore(..., "day")` against a zoned `now` would mix two
  // offsets and drop or keep a whole day at the boundary.
  if (dayKey(current) < dayKey(nowInZone(zone))) return true;
  return !getWorkingDayFor(workingHours, current)?.isWorkingDay;
};

/**
 * `disabledTime` for a picker: keeps the selection inside the location's open
 * hours for the selected day, and — on today — after the current moment.
 *
 * @param {Array}  workingHours  rows of { weekDay, isWorkingDay, startTime, endTime }
 * @param {any}    selectedDate  the date chosen in the paired DatePicker
 * @param {string} zone          the schedule's timezone, so "already past"
 *                               is judged on the location's clock
 */
export const getWorkingHoursDisabledTime = (workingHours, selectedDate, zone) => {
  const allHours = [...Array(24).keys()];
  if (!selectedDate) return { disabledHours: () => allHours };

  const workingDay = getWorkingDayFor(workingHours, selectedDate);
  if (!workingDay?.isWorkingDay) return { disabledHours: () => allHours };

  const start = parseWorkingTime(workingDay.startTime);
  const end = parseWorkingTime(workingDay.endTime);
  if (start.hour === null || end.hour === null) return {};

  const now = nowInZone(zone);
  const isToday = dayKey(selectedDate) === dayKey(now);

  return {
    disabledHours: () =>
      allHours.filter(hour => {
        if (hour < start.hour || hour > end.hour) return true;
        // The closing hour stays enabled — closing time itself is a valid slot
        // (9-7 means 7:00 PM can be picked, not 6:59 PM). `disabledMinutes`
        // below then blocks everything AFTER `end.minute` within that hour.
        // Disabling the whole hour here also pre-empted that, so the one valid
        // minute was unreachable.
        return isToday && hour < now.hour();
      }),
    disabledMinutes: hour => {
      const disabled = new Set();
      if (hour === start.hour) {
        for (let m = 0; m < start.minute; m += 1) disabled.add(m);
      }
      if (hour === end.hour) {
        for (let m = end.minute + 1; m < 60; m += 1) disabled.add(m);
      }
      if (isToday && hour === now.hour()) {
        for (let m = 0; m <= now.minute(); m += 1) disabled.add(m);
      }
      return [...disabled];
    },
  };
};

/**
 * The same wall clock, seen from the VIEWER's own timezone.
 *
 * A schedule is picked and saved in the location's zone, so someone in another
 * zone has no idea when it actually goes out — "11:00 PM" at a Denver rooftop
 * is 10:30 AM the next day in Kolkata. This renders that second reading so the
 * form can show both.
 *
 * @param {string|Date} wallClock the picked value — digits in `zone`, NOT UTC
 * @param {string} zone           the schedule's timezone
 * @param {string} format         one of DATE_TIME_FORMATS
 * @returns {{time: string, zone: string}|null} null when there is nothing
 *   useful to add: no value, no zone, or the viewer is already in that zone
 */
export const viewerZoneEquivalent = (
  wallClock,
  zone,
  format = DATE_TIME_FORMATS.MMM_DD_YYYY_COMMA_hh_mm_A
) => {
  if (!wallClock || !zone) return null;
  const viewerZone = dayjs.tz.guess();
  // Nothing to explain when both clocks agree.
  if (!viewerZone || viewerZone === zone) return null;
  try {
    // Parse the digits AS that zone (not UTC, not local), then re-read locally.
    const digits = dayjs(wallClock).format(DATE_TIME_FORMATS.YYYY_MM_DD_T_HH_MM_SS);
    const inViewerZone = dayjs.tz(digits, zone).local();
    if (!inViewerZone.isValid()) return null;
    return { time: inViewerZone.format(format), zone: viewerZone };
  } catch (e) {
    // Unknown zone string — drop the extra line rather than break the form.
    return null;
  }
};

/**
 * The timezone a schedule is expressed in.
 *
 *   - a single location selected -> THAT location's timezone;
 *   - "All locations" (the selector stores the ids comma-joined) -> there is no
 *     single active rooftop, so the LEAD's own location decides. A Denver user
 *     looking at the combined list must not schedule a Phoenix lead in Denver
 *     time.
 *
 * Neither is a fallback for the other: the rooftop you have selected is the one
 * you are scheduling as.
 *
 * @param {Object} user  current user (expects `locations[].location`)
 * @param {Object} leadLocation  the selected lead's `location` object
 */
export const getScheduleTimezone = (user, leadLocation) => {
  // "All locations" — no single active rooftop, so the lead's own location decides.
  if (isAllLocationSelected()) return leadLocation?.timezone;
  const currentLocationId = sessionStorage.getItem("currentLocationId");
  return user?.locations?.find(item => +item?.location?.id === +currentLocationId)?.location
    ?.timezone;
};

/**
 * Validate if selected date is in the future (for form validation)
 * @param {dayjs.Dayjs|string|Date} date - Date to validate
 * @param {string} errorMessage - Custom error message
 * @returns {Promise} Promise that resolves or rejects with error
 */
export const validateFutureDate = (date, errorMessage = "Please select a future date") => {
  if (date && isPastDate(date)) {
    return Promise.reject(new Error(errorMessage));
  }
  return Promise.resolve();
};

/**
 * Validate if selected datetime is in the future (for form validation)
 * @param {dayjs.Dayjs|string|Date} date - Date part
 * @param {dayjs.Dayjs|string|Date} time - Time part
 * @param {string} errorMessage - Custom error message
 * @returns {Promise} Promise that resolves or rejects with error
 */
export const validateFutureDateTime = (
  date,
  time,
  errorMessage = "Please select a future time"
) => {
  if (date && time && isPastDateTime(date, time)) {
    return Promise.reject(new Error(errorMessage));
  }
  return Promise.resolve();
};

/**
 * Calculate date range based on filter type.
 * @param {string} dateFilterType - Filter type (TODAY, YESTERDAY, THIS_WEEK, etc.)
 * @param {Array} customDateRange - Custom date range [startDate, endDate] for CUSTOM filter
 * @returns {Array} Date range as [startDate, endDate] in dayjs format
 */
/**
 * Format date to UTC ISO string for API requests.
 * Converts date to UTC timezone preserving the calendar date.
 * @param {dayjs.Dayjs|Date|string} date - Date to format
 * @param {boolean} isEndDate - If true, sets time to end of day (23:59:59.999Z)
 * @returns {string} ISO 8601 string in UTC
 */
export const formatDateForAPI = (date, isEndDate = false) => {
  if (!date) return "";

  const dayjsDate = dayjs(date);
  const dateStr = dayjsDate.format("YYYY-MM-DD");

  if (isEndDate) {
    return dayjs.utc(`${dateStr}T23:59:59.999Z`).toISOString();
  }
  return dayjs.utc(`${dateStr}T00:00:00.000Z`).toISOString();
};

/**
 * Converts time value to human-readable duration format.
 * Supports days, hours, minutes, and seconds.
 *
 * @param {number|string} value - Time value to convert
 * @param {string} inputFormat - Input format: "minutes" | "seconds" | "hours" (default: "minutes")
 * @returns {string} Formatted duration (e.g., "2d 5h 30m 15s", "1h 45m", "30s")
 *
 * @example
 * formatDurationFromTime(5000, "minutes") // "3d 11h 20m"
 * formatDurationFromTime(3661, "seconds") // "1h 1m 1s"
 * formatDurationFromTime(25.5, "hours")   // "1d 1h 30m"
 */
export const formatDurationFromTime = (value, inputFormat = "minutes") => {
  if (!value || value === 0) {
    return inputFormat === "seconds" ? "0s" : "0m";
  }

  const numValue = parseFloat(value);

  if (isNaN(numValue) || numValue < 0) {
    return inputFormat === "seconds" ? "0s" : "0m";
  }

  // Convert everything to seconds first
  let totalSeconds;
  switch (inputFormat) {
    case "hours":
      totalSeconds = numValue * 3600;
      break;
    case "seconds":
      totalSeconds = numValue;
      break;
    case "minutes":
    default:
      totalSeconds = numValue * 60;
      break;
  }

  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  const parts = [];

  if (days > 0) {
    parts.push(`${days}d`);
  }

  if (hours > 0) {
    parts.push(`${hours}h`);
  }

  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }

  // Show seconds only if total duration is less than 1 hour or if it's the only unit
  if (seconds > 0 && days === 0 && hours === 0) {
    parts.push(`${seconds}s`);
  }

  // If no parts (very small value rounded to 0), show appropriate zero
  if (parts.length === 0) {
    return inputFormat === "seconds" ? "0s" : "0m";
  }

  return parts.join(" ");
};

/**
 * Parses a human-readable duration string to minutes.
 * Supports: days (d), hours (h/hr), minutes (m/min), seconds (s/sec)
 *
 * @param {string} durationStr - Duration string (e.g., "5d 1h 30m 15s", "2hr 45min", "90m")
 * @returns {number} Total duration in minutes (decimal for partial minutes)
 *
 * @example
 * parseDurationToMinutes("5d 1h 30m")    // 7290
 * parseDurationToMinutes("2hr 30min")    // 150
 * parseDurationToMinutes("90s")          // 1.5
 * parseDurationToMinutes("1d")           // 1440
 */
export const parseDurationToMinutes = durationStr => {
  if (!durationStr || typeof durationStr !== "string") {
    return 0;
  }

  const str = durationStr.toLowerCase().trim();

  // If it's just a number, treat as minutes
  if (/^\d+(\.\d+)?$/.test(str)) {
    return parseFloat(str);
  }

  let totalMinutes = 0;

  // Match days: 5d, 5day, 5days
  const daysMatch = str.match(/(\d+(?:\.\d+)?)\s*d(?:ay|ays)?/);
  if (daysMatch) {
    totalMinutes += parseFloat(daysMatch[1]) * 24 * 60;
  }

  // Match hours: 5h, 5hr, 5hrs, 5hour, 5hours
  const hoursMatch = str.match(/(\d+(?:\.\d+)?)\s*h(?:r|rs|our|ours)?/);
  if (hoursMatch) {
    totalMinutes += parseFloat(hoursMatch[1]) * 60;
  }

  // Match minutes: 5m, 5min, 5mins, 5minute, 5minutes (but not if followed by 'o' for month)
  const minutesMatch = str.match(/(\d+(?:\.\d+)?)\s*m(?:in|ins|inute|inutes)?(?![o])/);
  if (minutesMatch) {
    totalMinutes += parseFloat(minutesMatch[1]);
  }

  // Match seconds: 5s, 5sec, 5secs, 5second, 5seconds
  const secondsMatch = str.match(/(\d+(?:\.\d+)?)\s*s(?:ec|ecs|econd|econds)?/);
  if (secondsMatch) {
    totalMinutes += parseFloat(secondsMatch[1]) / 60;
  }

  return totalMinutes;
};

/**
 * Format a dashboard date filter as a display label with the resolved range.
 *   "Month to date" → "Month to date (May 1, 2026 - May 18, 2026)"
 *   "Last week"     → "Last week (May 11, 2026 - May 18, 2026)"
 *   "custom"        → "May 1, 2026 - May 18, 2026" (range only, no label)
 *
 * "Month to date" / "this week" semantics: start of period → today (not end of
 * period), which is what the dashboard actually queries.
 *
 * @param {string} filter - Filter value (TODAY / THIS_WEEK / THIS_MONTH / ...).
 * @param {Array<string|dayjs.Dayjs>} [customRange] - Used only when filter === "custom".
 * @returns {string}
 */
export const formatFilterDateRangeLabel = (filter, customRange = null) => {
  const fmt = d => d.format(DATE_TIME_FORMATS.MMM_DD_YYYY);

  if (filter === "custom") {
    if (Array.isArray(customRange) && customRange[0] && customRange[1]) {
      // Local-TZ parse: the payload encodes local-midnight-shifted-to-UTC,
      // so local parsing recovers the picker's selected calendar date for
      // the same-viewer case (matches what the picker UI shows).
      const start = dayjs(customRange[0]);
      const end = dayjs(customRange[1]);
      if (start.isValid() && end.isValid()) return `${fmt(start)} - ${fmt(end)}`;
    }
    return "";
  }

  const labelByFilter = {
    TODAY: "Today",
    YESTERDAY: "Yesterday",
    THIS_WEEK: "This Week",
    LAST_WEEK: "Last Week",
    THIS_MONTH: "Month to date",
    LAST_MONTH: "Last Month",
    LAST_30_DAYS: "Last 30 Days",
  };
  const label = labelByFilter[filter] || "";

  const today = dayjs().startOf("day");
  let start;
  let end;

  switch (filter) {
    case "TODAY":
      start = today;
      end = today;
      break;
    case "YESTERDAY":
      start = today.subtract(1, "day");
      end = start;
      break;
    case "THIS_WEEK":
      // Weeks start Monday (ISO 8601), not Sunday.
      start = today.startOf("isoWeek");
      end = today;
      break;
    case "LAST_WEEK": {
      const lw = today.subtract(1, "week");
      start = lw.startOf("isoWeek");
      end = lw.endOf("isoWeek").startOf("day");
      break;
    }
    case "THIS_MONTH":
      // Month to date: from the 1st of this month up to today.
      start = today.startOf("month");
      end = today;
      break;
    case "LAST_MONTH": {
      const lm = today.subtract(1, "month");
      start = lm.startOf("month");
      end = lm.endOf("month").startOf("day");
      break;
    }
    case "LAST_30_DAYS":
      // Inclusive of today: 30 days total (today + 29 previous).
      start = today.subtract(29, "day");
      end = today;
      break;
    default:
      return label;
  }

  if (!start?.isValid() || !end?.isValid()) return label;
  return `${label} (${fmt(start)} - ${fmt(end)})`;
};

/**
 * Same resolved range as `formatFilterDateRangeLabel`, but returns ONLY the
 * `MMM D, YYYY - MMM D, YYYY` portion (no preset label / no parentheses).
 * Used for the tooltip on the date filter select where the option label is
 * already visible — duplicating it in the tooltip would be noise.
 *
 * @param {string} filter
 * @param {Array<string|dayjs.Dayjs>} [customRange]
 * @returns {string}
 */
export const formatFilterDateRangeOnly = (filter, customRange = null) => {
  const full = formatFilterDateRangeLabel(filter, customRange);
  if (!full) return "";
  // Preset: "Month to date (May 1, 2026 - May 18, 2026)" → "May 1, 2026 - May 18, 2026"
  const parenMatch = full.match(/\(([^)]+)\)\s*$/);
  if (parenMatch) return parenMatch[1];
  // Custom: full is already just the range.
  return full;
};

/**
 * Returns ONLY the preset label (e.g. "Today", "Month to date", "Last 30 Days").
 * Strips the resolved-range portion that `formatFilterDateRangeLabel` appends,
 * so callers can render the label inline (e.g. next to a "date basis" dropdown)
 * without duplicating the range that's already shown via the date-filter
 * select's hover tooltip.
 *
 * @param {string} filter
 * @returns {string}
 */
export const formatFilterLabelOnly = filter => {
  const labelByFilter = {
    TODAY: "Today",
    YESTERDAY: "Yesterday",
    THIS_WEEK: "This Week",
    LAST_WEEK: "Last Week",
    THIS_MONTH: "Month to date",
    LAST_MONTH: "Last Month",
    LAST_30_DAYS: "Last 30 Days",
  };
  return labelByFilter[filter] || "";
};

export const getDateRangeFromFilter = (dateFilterType, customDateRange = null) => {
  const today = dayjs().startOf("day");

  switch (dateFilterType) {
    case "TODAY":
      return [today, today];

    case "YESTERDAY": {
      const yesterday = today.subtract(1, "day");
      return [yesterday, yesterday];
    }

    case "THIS_WEEK":
      // Weeks start Monday (ISO 8601), not Sunday.
      return [today.startOf("isoWeek"), today.endOf("isoWeek").startOf("day")];

    case "LAST_WEEK": {
      const lastWeek = today.subtract(1, "week");
      return [lastWeek.startOf("isoWeek"), lastWeek.endOf("isoWeek").startOf("day")];
    }

    case "THIS_MONTH":
      return [today.startOf("month"), today.endOf("month").startOf("day")];

    case "LAST_MONTH": {
      const lastMonth = today.subtract(1, "month");
      return [lastMonth.startOf("month"), lastMonth.endOf("month").startOf("day")];
    }

    case "LAST_30_DAYS":
      // Inclusive of today: 30 days total (today + 29 previous).
      return [today.subtract(29, "day"), today];

    case "custom":
      return customDateRange;

    default:
      return customDateRange;
  }
};
